/** Documentation building blocks: one language choice for every code block, steady prose width. */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { CodeBlock, PageHeader, type CodeSample, type Crumb } from '@p1plug/ui';

const KEY = 'p1plug.docs.language';
const LanguageContext = createContext<{ language: string; setLanguage: (l: string) => void }>({
  language: 'cURL',
  setLanguage: () => {},
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, set] = useState(() => {
    try {
      return localStorage.getItem(KEY) ?? 'cURL';
    } catch {
      return 'cURL';
    }
  });
  const setLanguage = (l: string) => {
    set(l);
    try {
      localStorage.setItem(KEY, l);
    } catch {
      // The choice then lasts for this visit.
    }
  };
  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
}

/** A code example that follows the reader's language across the whole site. */
export function Code({ samples }: { samples: CodeSample[] }) {
  const { language, setLanguage } = useContext(LanguageContext);
  return <CodeBlock samples={samples} language={language} onLanguage={setLanguage} />;
}

/** A fixed block: a response, an HTTP exchange, a payload. */
export function Snippet({ title, code }: { title: string; code: string }) {
  return <CodeBlock samples={[{ language: title, code }]} />;
}

export function DocPage({
  title,
  lead,
  crumbs,
  children,
}: {
  title: string;
  lead: string;
  crumbs?: Crumb[];
  children: ReactNode;
}) {
  return (
    <article className="doc">
      <PageHeader title={title} lead={lead} crumbs={crumbs} />
      {children}
    </article>
  );
}

export function H2({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h2 className="p1x-section-title doc-h2" id={id}>
      {children}
    </h2>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="doc-p">{children}</p>;
}

export const C = ({ children }: { children: ReactNode }) => <code className="p1x-inline-code">{children}</code>;
